/**
 * Bakes a text-on-path's perpendicular offset into the path GEOMETRY (WMPCM-10320 Bug 3).
 *
 * ## Why this exists
 *
 * The obvious way to lift text off its path is to shift each glyph perpendicular to the local
 * tangent — `<tspan dy>` on a `<textPath>`, which is what this codebase shipped. That is correct
 * on a smooth outline and **wrong at a corner**: the glyph anchors keep their spacing along the
 * ORIGINAL path, but after the perpendicular shift the ones inside a concave corner are closer
 * together than their own advance widths, so they overlap. At a convex corner they fan apart and
 * leave a gap.
 *
 * A star is nothing but corners, which is why it reads as duplicated, piled-up characters.
 *
 * **This module does not make a star's notches clean, and nothing at this layer can.** It fixes the
 * TIPS. See "What it cannot fix" at the bottom before quoting any number here as a pass.
 *
 * Measured on a 200×200 five-point star at 12pt text (≈7pt advance) with offset 6. The metric is
 * spacing between adjacent glyph ANCHORS, so it detects a path that crowds glyph origins; it does
 * NOT detect two glyph BOXES intersecting after they rotate apart at a corner, which is a different
 * failure and the one left open below:
 *
 * | path the glyphs ride              | glyphs that fit | worst adjacent spacing | collisions |
 * | --------------------------------- | --------------- | ---------------------- | ---------- |
 * | per-glyph `dy` (the old way)      | 101             | **1.21pt** (17%)       | **5**      |
 * | offset 0, on the outline          | 101             | 2.41pt (34%)           | 2          |
 * | baked offset, mitered corners     | 120             | 4.27pt (61%)           | 0          |
 * | baked offset, ROUND convex joins  | 106             | 5.89pt (84%)           | 0          |
 *
 * Five collisions, one per inner notch. Note the second row: the offset is what turns a survivable
 * 2.41pt into an unreadable 1.21pt, so this module is what makes a non-zero default offset
 * (`TEXT_ON_PATH_DEFAULT_OFFSET_PT`, Bug 1 of the same ticket) safe to ship at all. And note the
 * last two: mitering alone still left a visible pinch at the tips, which is why the convex joins
 * are arcs — see `offsetPolylinePoints`. Every other outline lands at 94% or better; a circle,
 * having no corners, is unchanged at 100%.
 *
 * ## What it cannot fix
 *
 * A star's inner notches still overlap, and `<textPath>` cannot avoid it. The path turns 111° there
 * and a glyph is a BOX, not a point: at 12pt it is about 8.6pt tall, so two neighbours rotating
 * apart across that turn sweep into each other however far apart their anchors are. Measured as
 * box intersection rather than anchor spacing, the worst pair overlaps by roughly a full glyph
 * width, and filleting the notch only halves it — a 12pt radius leaves ~70%, a 20pt radius ~54%,
 * and clearing it entirely would need a radius larger than the star itself.
 *
 * Legacy Page Editor renders the identical overlap on the same star (reproduced in its own
 * reference demo, 2026-09-08), so this is inherited, not introduced. Treat it as a known limit of
 * running text round a sharp concave corner: the mitigations are a smaller point size, fewer
 * characters, or a shape without notches.
 *
 * ## What it does instead
 *
 * The same thing legacy Page Editor does — see `offsetPolylinePoints` in the reference demo, whose
 * comment states it outright: both its path builders "bake `top.offset` into the path geometry
 * itself (mitered at corners) rather than applying it per-character". Offsetting every vertex along
 * its own edge normal is not enough: two adjacent offset edges no longer meet, leaving a wedge at
 * convex corners and a self-crossing at concave ones. Each interior vertex is therefore re-placed
 * at the MITER point — where the two offset edges, extended as infinite lines, intersect — exactly
 * like a stroke `miter` line join. That is what keeps the offset path, and the text riding it,
 * continuous through a corner.
 *
 * Curves are flattened first. That is legacy's model too (it works on point lists throughout), and
 * it is what makes one miter rule cover every outline this editor draws.
 */

import { parseSvgPathSubpaths } from './svg-path-parse'
import type { SvgPathSubpath } from '../types/svg-path-subpath'
import type { FlatPoint } from '../types/flat-point'
import type { FlatSubpath } from '../types/flat-subpath'
import type { OffsetSvgPath } from '../types/offset-svg-path'

/**
 * Line segments each curve is approximated by.
 *
 * 24 is chosen against the thing that actually degrades: a chord's sagitta. On the largest outline
 * this editor allows (`EDITOR_PASTEBOARD_SIZE_PT`, 6000pt) a quarter-circle at 24 steps deviates
 * from the true arc by well under a tenth of a point — far below a glyph's own positioning
 * tolerance — while keeping a full rounded rectangle under a hundred points.
 */
const CURVE_STEPS = 24

/** Below this, an offset is not worth a second path — see `offsetSvgPath`. */
const MIN_OFFSET_PT = 0.01

/** Legacy's own limit: past this a needle-thin corner's miter spikes out absurdly far. */
const MITER_LIMIT_FACTOR = 4

/**
 * Line segments a convex corner's arc is sampled at, per 90° of turn.
 *
 * The arc's radius is the offset itself — 6pt by default — so eight per quarter turn puts the
 * samples about 1.2pt apart, well under a glyph's advance. The text shaper never sees the facets.
 */
const JOIN_ARC_STEPS_PER_QUARTER = 8

/** Two points closer than this are the same point as far as an edge direction is concerned. */
const EPSILON = 1e-9

function quadraticAt(p0: FlatPoint, c: FlatPoint, p1: FlatPoint, t: number): FlatPoint {
  const u = 1 - t

  return {
    x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x,
    y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y,
  }
}

function cubicAt(p0: FlatPoint, c1: FlatPoint, c2: FlatPoint, p1: FlatPoint, t: number): FlatPoint {
  const u = 1 - t

  return {
    x: u * u * u * p0.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * p1.x,
    y: u * u * u * p0.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * p1.y,
  }
}

/**
 * Sample an SVG elliptical arc, converting endpoint parameterisation to centre parameterisation
 * per SVG 1.1 appendix F.6.5.
 *
 * Arcs are not hypothetical here: `roundedRectPathD` writes four of them for a rounded rectangle,
 * and a fully-rounded one collapses to a two-arc circle — both perfectly ordinary text-on-path
 * targets. Returning early on an arc would have left exactly those shapes on the broken per-glyph
 * path with nothing to say why.
 */
function arcPoints(
  from: FlatPoint,
  rxIn: number,
  ryIn: number,
  rotationDeg: number,
  largeArc: number,
  sweep: number,
  to: FlatPoint
): FlatPoint[] {
  // Degenerate radii: the spec says treat the arc as a straight line.
  if (Math.abs(rxIn) < EPSILON || Math.abs(ryIn) < EPSILON) return [to]

  const phi = (rotationDeg * Math.PI) / 180
  const cosPhi = Math.cos(phi)
  const sinPhi = Math.sin(phi)
  const dx2 = (from.x - to.x) / 2
  const dy2 = (from.y - to.y) / 2
  const x1p = cosPhi * dx2 + sinPhi * dy2
  const y1p = -sinPhi * dx2 + cosPhi * dy2

  let rx = Math.abs(rxIn)
  let ry = Math.abs(ryIn)

  // F.6.6: scale the radii up when they are too small to span the endpoints.
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry)

  if (lambda > 1) {
    const scale = Math.sqrt(lambda)

    rx *= scale
    ry *= scale
  }

  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p
  const factor = (largeArc !== sweep ? 1 : -1) * Math.sqrt(Math.max(0, num / den))
  const cxp = (factor * (rx * y1p)) / ry
  const cyp = (factor * -(ry * x1p)) / rx
  const cx = cosPhi * cxp - sinPhi * cyp + (from.x + to.x) / 2
  const cy = sinPhi * cxp + cosPhi * cyp + (from.y + to.y) / 2

  const angleOf = (ux: number, uy: number): number => Math.atan2(uy, ux)
  const theta1 = angleOf((x1p - cxp) / rx, (y1p - cyp) / ry)
  const theta2 = angleOf((-x1p - cxp) / rx, (-y1p - cyp) / ry)

  let delta = theta2 - theta1

  if (sweep === 0 && delta > 0) delta -= 2 * Math.PI
  if (sweep === 1 && delta < 0) delta += 2 * Math.PI

  // Proportional to the sweep so a 90° corner is not sampled as finely as a full circle.
  const steps = Math.max(2, Math.ceil((Math.abs(delta) / (Math.PI / 2)) * (CURVE_STEPS / 4)))
  const out: FlatPoint[] = []

  for (let i = 1; i <= steps; i++) {
    const theta = theta1 + (delta * i) / steps
    const cosT = Math.cos(theta)
    const sinT = Math.sin(theta)

    out.push({
      x: cosPhi * rx * cosT - sinPhi * ry * sinT + cx,
      y: sinPhi * rx * cosT + cosPhi * ry * sinT + cy,
    })
  }

  return out
}

/**
 * The convex-corner arc: every point at `|offset|` from the vertex between the two offset edge
 * ends, swept the short way round.
 *
 * Endpoints included, so the caller emits this in place of the vertex and the arc meets both
 * straight runs exactly.
 */
function joinArcPoints(centre: FlatPoint, from: FlatPoint, to: FlatPoint): FlatPoint[] {
  const radius = Math.hypot(from.x - centre.x, from.y - centre.y)

  if (radius < EPSILON) return [from]

  const start = Math.atan2(from.y - centre.y, from.x - centre.x)
  const end = Math.atan2(to.y - centre.y, to.x - centre.x)
  let delta = end - start

  // The short way. A convex join never exceeds a half turn, so the wide way is always wrong.
  while (delta > Math.PI) delta -= 2 * Math.PI
  while (delta < -Math.PI) delta += 2 * Math.PI

  const steps = Math.max(
    2,
    Math.ceil((Math.abs(delta) / (Math.PI / 2)) * JOIN_ARC_STEPS_PER_QUARTER)
  )
  const arc: FlatPoint[] = []

  for (let i = 0; i <= steps; i++) {
    const theta = start + (delta * i) / steps

    arc.push({ x: centre.x + Math.cos(theta) * radius, y: centre.y + Math.sin(theta) * radius })
  }

  return arc
}

function flattenSubpath(subpath: SvgPathSubpath): FlatSubpath {
  let cursor: FlatPoint = { x: subpath.startX, y: subpath.startY }
  const points: FlatPoint[] = [cursor]

  for (const segment of subpath.segments) {
    if (segment.type === 'L') {
      cursor = { x: segment.x, y: segment.y }
      points.push(cursor)
      continue
    }

    if (segment.type === 'Q') {
      const p0 = cursor
      const c = { x: segment.x1, y: segment.y1 }
      const p1 = { x: segment.x, y: segment.y }

      for (let i = 1; i <= CURVE_STEPS; i++) points.push(quadraticAt(p0, c, p1, i / CURVE_STEPS))
      cursor = p1
      continue
    }

    if (segment.type === 'C') {
      const p0 = cursor
      const c1 = { x: segment.x1, y: segment.y1 }
      const c2 = { x: segment.x2, y: segment.y2 }
      const p1 = { x: segment.x, y: segment.y }

      for (let i = 1; i <= CURVE_STEPS; i++) points.push(cubicAt(p0, c1, c2, p1, i / CURVE_STEPS))
      cursor = p1
      continue
    }

    const to = { x: segment.x, y: segment.y }

    points.push(
      ...arcPoints(
        cursor,
        segment.rx,
        segment.ry,
        segment.rotation,
        segment.largeArc,
        segment.sweep,
        to
      )
    )
    cursor = to
  }

  return { points: dedupe(points), closed: subpath.closed }
}

/**
 * Drop repeated points, and on a closed subpath the explicit return to the start.
 *
 * Both are ordinary in real path data — `roundedRectPathD` ends on the start point before its `Z`,
 * and a degenerate corner radius collapses a whole arc to a single point. A duplicate has no edge
 * direction, so leaving it in would put a zero-length edge into the normal table and pick an
 * arbitrary miter through it.
 */
function dedupe(points: FlatPoint[]): FlatPoint[] {
  const out: FlatPoint[] = []

  for (const point of points) {
    const last = out[out.length - 1]

    if (last && Math.abs(last.x - point.x) < EPSILON && Math.abs(last.y - point.y) < EPSILON) {
      continue
    }

    out.push(point)
  }

  const first = out[0]
  const last = out[out.length - 1]

  if (
    out.length > 1 &&
    first &&
    last &&
    Math.abs(first.x - last.x) < EPSILON &&
    Math.abs(first.y - last.y) < EPSILON
  ) {
    out.pop()
  }

  return out
}

/** Flatten every subpath of `d` to a polyline. `null` when `d` is not parseable path data. */
export function flattenSvgPath(d: string): FlatSubpath[] | null {
  const subpaths = parseSvgPathSubpaths(d)

  if (subpaths == null) return null

  const flat = subpaths.map(flattenSubpath).filter((s) => s.points.length >= 2)

  return flat.length === 0 ? null : flat
}

/** Arc length of a polyline, counting the closing edge when `closed`. */
export function polylineLength(points: FlatPoint[], closed: boolean): number {
  let total = 0

  for (let i = 0; i < points.length - 1; i++) {
    total += Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y)
  }

  if (closed && points.length > 1) {
    const first = points[0]
    const last = points[points.length - 1]

    total += Math.hypot(first.x - last.x, first.y - last.y)
  }

  return total
}

/**
 * Move a polyline `offset` points along its own left normal, joining the moved edges at every
 * interior vertex.
 *
 * Two different joins, because a corner's two offset edges do two different things:
 *
 *  - **Convex** (the offset side is the outside of the turn — a star's tips): the edges pull APART,
 *    leaving a wedge. Filled with a circular ARC of radius `|offset|` centred on the original
 *    vertex, which is the true offset curve — the boundary of the shape grown by a disc.
 *  - **Concave** (a star's notches): the edges CROSS. Their intersection is where the offset path
 *    genuinely turns, so that miter point is used, capped at `MITER_LIMIT_FACTOR` for a needle.
 *
 * Legacy miters both, and that is what still crowded text at a sharp tip after the offset was first
 * baked in. A miter's reach is `|offset| / sin(θ/2)`, so a five-point star's 39° tip spikes THREE
 * times the offset — 18pt for the default 6 — and text has to run out to that point, turn 141° on a
 * knife edge and come back, landing two glyphs almost on top of each other. Measured on a 200×200
 * star at 12pt (7pt advance), the tightest pair sat at 4.27pt with miters. The arc replaces that
 * 18pt spike with a 6pt curve of constant curvature, which text traverses at even spacing.
 *
 * Sign convention matches the `<tspan dy={-offset}>` this replaces: a positive offset lifts the
 * text to the same side it used to sit on, so switching to this path does not silently flip
 * existing artwork to the other side of its outline.
 *
 * An open polyline's two endpoints have only one adjacent edge each and are simply displaced along
 * it — there is no corner there to join.
 */
export function offsetPolylinePoints(
  points: FlatPoint[],
  offset: number,
  closed: boolean
): FlatPoint[] {
  if (offset === 0 || points.length < 2) return points.slice()

  const n = points.length
  const edgeCount = closed ? n : n - 1
  const dirs: FlatPoint[] = []
  const normals: FlatPoint[] = []

  for (let i = 0; i < edgeCount; i++) {
    const a = points[i]
    const b = points[(i + 1) % n]
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len = Math.hypot(dx, dy) || 1

    dirs.push({ x: dx / len, y: dy / len })
    // Left normal for a y-down coordinate system, which is what puts a positive offset on the same
    // side `dy={-offset}` used to.
    normals.push({ x: dy / len, y: -dx / len })
  }

  const miterLimit = Math.abs(offset) * MITER_LIMIT_FACTOR
  const out: FlatPoint[] = []

  for (let i = 0; i < n; i++) {
    // Endpoints of an open polyline: one edge, no corner.
    if (!closed && (i === 0 || i === n - 1)) {
      const edge = i === 0 ? 0 : edgeCount - 1

      out.push({
        x: points[i].x + normals[edge].x * offset,
        y: points[i].y + normals[edge].y * offset,
      })
      continue
    }

    const incoming = (i - 1 + edgeCount) % edgeCount
    const outgoing = i % edgeCount
    const p = points[i]
    const a1 = { x: p.x + normals[incoming].x * offset, y: p.y + normals[incoming].y * offset }
    const a2 = { x: p.x + normals[outgoing].x * offset, y: p.y + normals[outgoing].y * offset }
    const d1 = dirs[incoming]
    const d2 = dirs[outgoing]
    const denom = d1.x * d2.y - d1.y * d2.x

    // Which way the path turns, read against which side the offset is on. Same sign means the
    // offset is on the outside of the turn, so the edges separate and the wedge needs an arc.
    if (denom * offset > 0) {
      out.push(...joinArcPoints(p, a1, a2))
      continue
    }

    let miter = a1

    // Parallel edges (a straight-through vertex, or a doubled-back spike) have no intersection;
    // the plain offset point is already correct for the first of those and the least bad for the
    // second.
    if (Math.abs(denom) > 1e-6) {
      const t = ((a2.x - a1.x) * d2.y - (a2.y - a1.y) * d2.x) / denom

      miter = { x: a1.x + d1.x * t, y: a1.y + d1.y * t }
    }

    const distance = Math.hypot(miter.x - p.x, miter.y - p.y)

    if (distance > miterLimit && distance > 0) {
      const scale = miterLimit / distance

      miter = { x: p.x + (miter.x - p.x) * scale, y: p.y + (miter.y - p.y) * scale }
    }

    out.push(miter)
  }

  return out
}

/** Round-trip-safe formatting: drop the noise a float introduces without moving the point. */
function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(4)))
}

/**
 * `d`, moved `offset` points along its own normal with mitered corners.
 *
 * `null` — meaning "keep using the original path" — for an offset of zero, unparseable data, or a
 * path with nothing to offset. Every caller must handle it, and handling it is never a failure
 * mode: the original path is exactly what shipped before this module existed.
 *
 * A compound path is offset subpath by subpath. Each keeps its own open/closed state, since an open
 * subpath's endpoints must not be mitered against each other.
 */
export function offsetSvgPath(d: string, offset: number): OffsetSvgPath | null {
  if (!Number.isFinite(offset) || Math.abs(offset) < MIN_OFFSET_PT) return null

  const flat = flattenSvgPath(d)

  if (flat == null) return null

  const parts: string[] = []
  let length = 0
  let rawLength = 0

  for (const subpath of flat) {
    const moved = offsetPolylinePoints(subpath.points, offset, subpath.closed)

    rawLength += polylineLength(subpath.points, subpath.closed)
    length += polylineLength(moved, subpath.closed)

    const lineTos = moved
      .slice(1)
      .map((point) => ` L ${fmt(point.x)} ${fmt(point.y)}`)
      .join('')

    parts.push(`M ${fmt(moved[0].x)} ${fmt(moved[0].y)}${lineTos}${subpath.closed ? ' Z' : ''}`)
  }

  if (parts.length === 0 || length <= 0 || rawLength <= 0) return null

  return { d: parts.join(' '), length, rawLength }
}
